import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { apply } from "../../../Redux/themeSlice";
import {
  BrightnessMedium,
  Close,
  DarkMode,
  Info,
  LightMode,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  getCacRoute,
  getClientSettingsRoute,
  getModeratorCount,
  updateCacRoute,
  updateProfileRoute,
} from "../../../routes/apiRoutes";
import { toast } from "react-toastify";
import _ from "lodash";
import axiosInstance from "../../../axios/axiosInstance";
import { t } from "i18next";

const settingsCategories = [
  { category: "Theme", authority: ["all"] },
  { category: "Member", authority: ["Admin", "Super Admin"] },
  { category: "Moderation", authority: ["Client"] },
];

const propertyMapping = {
  cac: "cac",
  "points-increment": "transactionPercentage",
  "global-moderation-minimum-moderators": "minimumModeratorsSetting",
  "global-moderation-enabled": "moderationSetting",
};

export default function SettingsDialog(props) {
  const dispatch = useDispatch();
  const [settingsData, setSettingsData] = React.useState({
    cac: "",
    transactionPercentage: "",
    moderationSetting: false,
    minimumModeratorsSetting: 0,
  });
  const [oldData, setOldData] = React.useState({
    cac: "",
    transactionPercentage: "",
    moderationSetting: "false",
    minimumModeratorsSetting: "1",
  });
  const [errors, setErrors] = React.useState({
    cac: false,
    transactionPercentage: false,
    moderationSetting: false,
    minimumModeratorsSetting: false,
  });
  const [modules, setModules] = React.useState({ moderation: null });

  const [totalModerators, setTotalModerators] = React.useState(1);
  const colors = useSelector((state) => state.themeValues.colors);
  const currentUser = useSelector(
    (state) => state.authenticationValues.currentUser
  );
  const mainColor = useSelector((state) => state.themeValues.mainColor);
  const [loading, setLoading] = React.useState(false);
  const mode = useSelector((state) => state.themeValues.mode);
  const [oldThemeData, setOldThemeData] = React.useState({
    mainColor: mainColor,
    mode: mode,
  });
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = React.useState(
    settingsCategories[0].category
  );

  const handleCancel = () => {
    setSettingsData(oldData);
    setErrors({
      cac: false,
      transactionPercentage: false,
      moderationSetting: false,
      minimumModeratorsSetting: false,
    });
    props.setOpen(false);
  };

  React.useEffect(() => {
    if (settingsData.moderationSetting === true) {
      setErrors((prev) => ({
        ...prev,
        minimumModeratorsSetting: errorConditions["minimumModeratorsSetting"]?.(
          settingsData.minimumModeratorsSetting
        ),
      }));
    } else {
      setErrors((prev) => ({ ...prev, minimumModeratorsSetting: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalModerators]);

  const handleSettingChange = (value, item) => {
    setSettingsData((prev) => ({ ...prev, [item]: value.toString() }));
    checkErrors(item, value);

    // check minimum moderation settings too when moderation is enabled or disabled
    if (item === "moderationSetting" && value === false)
      setErrors((prev) => ({ ...prev, minimumModeratorsSetting: false }));
    if (item === "moderationSetting" && value === true)
      setErrors((prev) => ({
        ...prev,
        minimumModeratorsSetting: errorConditions["minimumModeratorsSetting"]?.(
          settingsData.minimumModeratorsSetting
        ),
      }));
  };

  const errorConditions = {
    cac: (value) => value < 0,
    transactionPercentage: (value) => value <= 0 || value > 100,
    minimumModeratorsSetting:
      settingsData.moderationSetting &&
      ((value) => value < 1 || value > totalModerators),
  };

  const checkErrors = (item, value) => {
    const hasError = errorConditions[item]?.(value);
    if (hasError) {
      setErrors((prev) => ({ ...prev, [item]: true }));
    } else {
      setErrors((prev) => ({ ...prev, [item]: false }));
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get(`${getCacRoute}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      response?.data?.response?.forEach((item) => {
        const stateKey = propertyMapping[item.propertyName];
        if (stateKey) {
          setSettingsData((prevValue) => ({
            ...prevValue,
            [stateKey]: item.value,
          }));
          setOldData((prevValue) => ({ ...prevValue, [stateKey]: item.value }));
        }
      });
    } catch (error) {
      if (error.response?.data?.statusCode === 401) {
        toast.error(
          `Unable to fetch settings. You need to re-login to perform this action.`,
          {
            autoClose: false,
          }
        );
      } else {
        toast.dismiss();
        toast.error(`Unable to fetch settings. Something went wrong.`, {
          autoClose: false,
        });
      }
    }
  };

  const fetchClientSettings = async () => {
    try {
      const response = await axiosInstance.get(`${getClientSettingsRoute}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = response?.data?.response;
      setSettingsData((prev) => ({
        ...prev,
        minimumModeratorsSetting: data.minimumModerators.toString(),
        moderationSetting: data.moderationEnabled.toString(),
      }));
      setOldData((prev) => ({
        ...prev,
        minimumModeratorsSetting: data.minimumModerators.toString(),
        moderationSetting: data.moderationEnabled.toString(),
      }));
      setModules((prev) => ({ ...prev, moderation: data.moderation }));
    } catch (error) {
      if (error.response?.data?.statusCode === 401) {
        toast.error(
          `Unable to fetch settings. You need to re-login to perform this action.`,
          {
            autoClose: false,
          }
        );
      } else {
        toast.dismiss();
        toast.error(`Unable to fetch settings. Something went wrong.`, {
          autoClose: false,
        });
      }
    }
  };

  const fetchModeratorCount = async () => {
    try {
      const response = await axiosInstance.get(`${getModeratorCount}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setTotalModerators(response.data?.response?.count);
    } catch (error) {
      if (error.response?.data?.statusCode === 401) {
        toast.error(
          `Unable to fetch settings. You need to re-login to perform this action.`,
          {
            autoClose: false,
          }
        );
      } else {
        toast.dismiss();
        toast.error(`Unable to fetch settings. Something went wrong.`, {
          autoClose: false,
        });
      }
    }
  };

  const fetchClientModeratorCount = async () => {
    try {
      const response = await axiosInstance.get(`${getModeratorCount}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setTotalModerators(response.data?.response?.count);
    } catch (error) {
      if (error.response?.data?.statusCode === 401) {
        toast.error(
          `Unable to fetch settings. You need to re-login to perform this action.`,
          {
            autoClose: false,
          }
        );
      } else {
        toast.dismiss();
        toast.error(`Unable to fetch settings. Something went wrong.`, {
          autoClose: false,
        });
      }
    }
  };

  const currentUserRoles = currentUser?.userRoles?.map(
    (role) => role?.role?.name
  );
  const filteredCategories = settingsCategories.filter(
    (item) =>
      item.authority.some((auth) => currentUserRoles?.includes(auth)) ||
      item.authority.includes("all")
  );
  React.useEffect(() => {
    if (
      currentUserRoles?.includes("Super Admin") ||
      currentUserRoles?.includes("Admin")
    ) {
      fetchSettings();
      fetchModeratorCount();
    } else if (currentUserRoles?.includes("Client")) {
      fetchClientSettings();
      fetchClientModeratorCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleMainColorClick = async (index) => {
    dispatch(apply({ colors: colors, mainColor: colors[index], mode: mode }));
  };

  const handleDarkModeChange = async (e, value) => {
    if (value !== null) {
      dispatch(apply({ colors: colors, mainColor: mainColor, mode: value }));
    }
  };

  const handleSaveData = async () => {
    try {
      const noErrors = Object.values(errors).every((error) => error === false);
      if (noErrors) {
        setLoading(true);
        const { data } = await axiosInstance.patch(
          `${updateProfileRoute}${currentUser.id}`,
          {
            theme: { mainColor: mainColor, mode: mode },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (data.statusCode === 200 || data.statusCode === 201) {
          setOldThemeData({ mainColor: mainColor, mode: mode });
        }
        let newData = [];
        const categoryNames = filteredCategories.map(
          (element) => element.category
        );
        if (currentUserRoles.includes("Client")) {
          const settingUpdateResponse = await axiosInstance.patch(
            `${getClientSettingsRoute}`,
            {
              moderationEnabled: settingsData.moderationSetting === "true",
              minimumModerators: Number(settingsData.minimumModeratorsSetting),
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (
            settingUpdateResponse.data.statusCode === 200 ||
            settingUpdateResponse.data.statusCode === 201
          ) {
            toast.dismiss();
            toast.success("Settings Modified Successfully", {
              autoClose: 1000,
            });
            setOldData(settingsData);
            props.setOpen(false);
          } else {
            toast.dismiss();
            toast.error(
              `Unexpected response received from server. Something went wrong.`,
              { autoClose: false }
            );
          }
        } else {
          if (categoryNames.includes("Member")) {
            newData.push(
              {
                propertyName: "cac",
                value: settingsData.cac,
              },
              {
                propertyName: "points-increment",
                value: settingsData.transactionPercentage,
              }
            );
          }

          if (categoryNames.includes("Moderation")) {
            newData.push(
              {
                propertyName: "global-moderation-enabled",
                value: settingsData.moderationSetting,
              },
              {
                propertyName: "global-moderation-minimum-moderators",
                value: settingsData.minimumModeratorsSetting,
              }
            );
          }
          if (!_.isEqual(oldData, settingsData) && newData.length > 0) {
            const settingUpdateResponse = await axiosInstance.patch(
              `${updateCacRoute}`,
              newData,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
            if (
              settingUpdateResponse.data.statusCode === 200 ||
              settingUpdateResponse.data.statusCode === 201
            ) {
              toast.dismiss();
              toast.success("Settings Modified Successfully", {
                autoClose: 1000,
              });
              setOldData(settingsData);
              props.setOpen(false);
            } else {
              toast.dismiss();
              toast.error(
                `Unexpected response received from server. Something went wrong.`,
                { autoClose: false }
              );
            }
          } else {
            props.setOpen(false);
          }
        }
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Settings could not be saved. Something went wrong.`, {
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTheme = async () => {
    if (currentUser.theme) {
      dispatch(
        apply({
          colors: colors,
          mainColor: oldThemeData.mainColor,
          mode: oldThemeData.mode,
        })
      );
    } else {
      dispatch(apply({ colors: colors, mainColor: colors[0], mode: "light" }));
    }
    handleCancel();
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={handleCancel}
        aria-labelledby="settings-dialog"
        aria-describedby="settings-dialog"
        maxWidth="sm"
      >
        <div
          dir={theme.direction}
          style={{
            paddingInline: theme.spacing(3),
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(1),
            position: "relative",
            minWidth: 300,
          }}
        >
          <Typography
            sx={{
              textAlign: "center",
              // py: 2,
              px: 2,
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
          >
            {t("settings")}
          </Typography>
          {/* <Box
            display={"flex"}
            sx={{
              px: 3,
              borderBottom: `1px solid ${theme.palette.cancelText.disabled}`,
            }}
          >
            {filteredCategories.map((setting) => {
              return (
                (setting.category !== "Moderation" ||
                  modules.moderation ||
                  !currentUserRoles.includes("Client")) && (
                  <div
                    key={setting.category}
                    onClick={() => setSelectedCategory(setting.category)}
                  >
                    <Typography
                      width={150}
                      sx={{
                        textAlign: "center",
                        px: 1,
                        pb: 1,
                        cursor: "pointer",
                        borderBottom:
                          selectedCategory === setting.category
                            ? `3px solid ${theme.palette.primary.main}`
                            : 0,
                      }}
                    >
                      {t(setting.category)}
                    </Typography>
                  </div>
                )
              );
            })}
          </Box> */}
          <DialogContent sx={{ height: 220 }}>
            <IconButton
              onClick={handleCancel}
              sx={{ position: "absolute", top: 20, right: 20 }}
            >
              <Close />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                px: 0.5,
                py: 1,
                pt: 2,
                height: "100%",
                // width: 800,
              }}
            >
              {selectedCategory === "Theme" && (
                <>
                  <Box>
                    <Typography
                      variant="h5"
                      fontSize={"1.2rem"}
                      fontWeight={500}
                      mb={1}
                    >
                      {t("darkmode")}
                    </Typography>
                    <ToggleButtonGroup
                      value={mode}
                      onChange={handleDarkModeChange}
                      size="small"
                      exclusive
                      sx={{
                        "& .MuiToggleButton-root": {
                          color: theme.palette.whiteblack.contrastText,
                          backgroundColor:
                            theme.palette.componentBackgrounds.new,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.componentBackgrounds.hover,
                          },
                          textTransform: "none",
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                          color: theme.palette.whiteblack.main,
                          backgroundColor: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <ToggleButton key="dark" value="dark">
                        <Box display={"flex"} gap={0.5} mr={0.5}>
                          <DarkMode sx={{ fontSize: 17 }} />
                          <Typography fontSize={"0.8rem"}>{t("on")}</Typography>
                        </Box>
                      </ToggleButton>
                      {/* <ToggleButton key="auto" value="auto">
                        <Box display={"flex"} gap={0.5} mr={0.5}>
                          <BrightnessMedium sx={{ fontSize: 17 }} />
                          <Typography fontSize={"0.8rem"}>
                            {t("auto")}
                          </Typography>
                        </Box>
                      </ToggleButton> */}
                      <ToggleButton key="light" value="light">
                        <Box display={"flex"} gap={0.5} mr={0.5}>
                        <LightMode sx={{ fontSize: 17 }} />
                          <Typography fontSize={"0.8rem"}>
                            {t("off")}
                          </Typography>
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {/* <Divider />
                  <Box>
                    <Typography
                      variant="h5"
                      fontSize={"1.2rem"}
                      fontWeight={500}
                    >
                      {t("themecolor")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, px: 0.5, py: 1 }}>
                      {colors.map((color, index) => {
                        return (
                          <Box
                            key={index}
                            width={40}
                            height={40}
                            onClick={() => handleMainColorClick(index)}
                            sx={{
                              background: color,
                              cursor: "pointer",
                              border:
                                color === mainColor
                                  ? `5px solid ${theme.palette.whiteblack.contrastText}`
                                  : 0,
                            }}
                          ></Box>
                        );
                      })}
                    </Box>
                  </Box>
                  <Divider /> */}
                </>
              )}
              {/* {(currentUserRoles?.includes("Super Admin") ||
                currentUserRoles?.includes("Admin")) &&
                selectedCategory === "Member" && (
                  <>
                    <Box>
                      <Typography
                        variant="h5"
                        fontSize={"1.2rem"}
                        fontWeight={500}
                      >
                        {t("cac")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, px: 0.5, py: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          sx={{ height: 38, width: "30%" }}
                          InputProps={{ inputProps: { min: 0 } }}
                          helperText={
                            errors.cac ? "Value cannot be negative" : ""
                          }
                          error={errors.cac}
                          value={Number(settingsData.cac)}
                          onChange={(e) =>
                            handleSettingChange(e.target.value, "cac")
                          }
                        ></TextField>
                      </Box>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography
                        variant="h5"
                        fontSize={"1.2rem"}
                        fontWeight={500}
                      >
                        {t("pointsearned")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, px: 0.5, py: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          sx={{ height: 38, width: "30%" }}
                          InputProps={{ inputProps: { min: 1, max: 100 } }}
                          helperText={
                            errors.transactionPercentage
                              ? "Value should be between 1 and 100"
                              : ""
                          }
                          error={errors.transactionPercentage}
                          value={Number(settingsData.transactionPercentage)}
                          onChange={(e) =>
                            handleSettingChange(
                              e.target.value,
                              "transactionPercentage"
                            )
                          }
                        ></TextField>
                      </Box>
                    </Box>
                  </>
                )} */}
              {currentUserRoles?.includes("Client") &&
                (modules.moderation || !currentUserRoles.includes("Client")) &&
                selectedCategory === "Moderation" && (
                  <>
                    <Box>
                      <Typography
                        variant="h5"
                        fontSize={"1.2rem"}
                        fontWeight={500}
                      >
                        {t("moderationswitch")}
                      </Typography>
                      <Switch
                        onChange={(e, v) =>
                          handleSettingChange(v, "moderationSetting")
                        }
                        checked={settingsData.moderationSetting === "true"}
                      ></Switch>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography
                        variant="h5"
                        fontSize={"1.2rem"}
                        fontWeight={500}
                      >
                        {t("minimumvotes")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, px: 0.5, py: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          disabled={settingsData.moderationSetting === "false"}
                          sx={{ height: 38, width: "30%" }}
                          InputProps={{
                            inputProps: { min: 1, max: totalModerators },
                          }}
                          helperText={
                            errors.minimumModeratorsSetting
                              ? `Maximum Value: ${totalModerators}, Minimum Value: 1`
                              : ""
                          }
                          error={errors.minimumModeratorsSetting}
                          value={Number(settingsData.minimumModeratorsSetting)}
                          onChange={(e) =>
                            handleSettingChange(
                              e.target.value,
                              "minimumModeratorsSetting"
                            )
                          }
                        ></TextField>
                      </Box>
                      {errors.minimumModeratorsSetting && (
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          mt={4}
                          gap={2}
                        >
                          <Info sx={{ color: "#007bff" }} />
                          <Typography variant="body2" color="textSecondary">
                            For moderation to function, the total number of
                            available moderators must be at least the selected
                            value.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              {/* <Box
                sx={{
                  mt: "auto",
                  display: "flex",
                  gap: 2,
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancelTheme}
                  disabled={loading}
                  sx={{
                    py: 1,
                    fontWeight: 600,
                    textTransform: "none",
                    width: "150px",
                    height: 40,
                  }}
                >
                  {t("cancel")}
                </Button>
                <LoadingButton
                  loading={loading}
                  onClick={handleSaveData}
                  variant="contained"
                  sx={{
                    width: "150px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    py: 1,
                    height: 40,
                  }}
                >
                  <span>{t("save")}</span>
                </LoadingButton>
              </Box> */}
            </Box>
          </DialogContent>
        </div>
      </Dialog>
    </React.Fragment>
  );
}
